import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { Link } from 'react-router-dom';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CapsoIcon from './UI/CapsoIcon';
import CountdownTimer from './UI/CountdownTimer';
import PhoneIcon from '@mui/icons-material/Phone';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideocamIcon from '@mui/icons-material/Videocam';
import GetAppIcon from '@mui/icons-material/GetApp';
import { Link as MuiLink } from '@mui/material';
import saveShippingLabel from './shippingLabelGenerator';

const ProcedureScheduledOrIngested = ({ curProcedure }) => {
	const mediaUrls = {
		patientBrochure: 'https://dxkyfmvzq01qm.cloudfront.net/mycapso/patient_brochure.pdf',
		morningPrepInstructions: 'https://dxkyfmvzq01qm.cloudfront.net/mycapso/prep_instruction_morning.pdf',
		afternoonPrepInstructions: 'https://dxkyfmvzq01qm.cloudfront.net/mycapso/prep_instruction_afternoon.pdf',
		ingestionInstructions: 'https://dxkyfmvzq01qm.cloudfront.net/mycapso/ingestion_instruction.pdf',
		retrievalInstructions: 'https://vimeo.com/873350154',
		openedCapsuleHolderWithCapsule: 'https://dxkyfmvzq01qm.cloudfront.net/mycapso/OpenedCapsuleHolderWithCapsule.jpeg',
		retrievalKit: 'https://dxkyfmvzq01qm.cloudfront.net/mycapso/RetrievalKit.jpeg',
		capsoCamBox: 'https://dxkyfmvzq01qm.cloudfront.net/mycapso/CapsoCamBox.png',
		capsoCamCapsule: 'https://dxkyfmvzq01qm.cloudfront.net/mycapso/CapsoCamCapsule.jpeg',
		fedexLogo: 'https://dxkyfmvzq01qm.cloudfront.net/mycapso/fedex.png'
	};
	
	const [selectedStep, setSelectedStep] = useState(() => {
		const now = new Date();
		if (curProcedure.status === 'ingested') {
			return 'retrieval';
		} else if (now > new Date(curProcedure.procedureDate)) {
			return 'ingestion';
		}
		return 'preps';
	});
	const [currentTime, setCurrentTime] = useState(new Date());
	
	// Calculate times for each step based on procedure date
	const procedureDate = new Date(curProcedure.procedureDate);
	const ingestionTime = curProcedure.ingestionTime ? new Date(curProcedure.ingestionTime) : procedureDate;
	const dayBefore = new Date(procedureDate);
	dayBefore.setDate(dayBefore.getDate() - 1);
	const threeDaysBefore = new Date(procedureDate); // Calculate 3 days before
	threeDaysBefore.setDate(threeDaysBefore.getDate() - 3);
	
	const isMorningProcedure = procedureDate.getHours() < 12;

	const downloadShippingLabel = () => {
		saveShippingLabel(curProcedure.sn, curProcedure.shippingLabel, curProcedure.shippingCarrier)
	}
	
	// Create all the step times for both morning and afternoon procedures
	const createStepTimes = () => {
		const steps = [];
		const prevDay = new Date(dayBefore);
		const procDay = new Date(procedureDate);
		
		if (isMorningProcedure) {
			// Morning procedure steps
			const prevDayNoon = new Date(prevDay);
			prevDayNoon.setHours(12, 0, 0);
			
			const prevDay7pm = new Date(prevDay);
			prevDay7pm.setHours(19, 0, 0);
			
			const prevDay930pm = new Date(prevDay);
			prevDay930pm.setHours(21, 30, 0);
			
			const prevDay10pm = new Date(prevDay);
			prevDay10pm.setHours(22, 0, 0);
			
			const procDay2hrBefore = new Date(procedureDate);
			procDay2hrBefore.setHours(procDay2hrBefore.getHours() - 2);
			
			// Prep steps
			steps.push({
				category: 'preps',
				id: 'light-lunch',
				time: prevDayNoon,
				label: 'Light lunch',
				description: 'Have a light lunch',
				completed: isStepCompleted(prevDayNoon)
			});
			
			steps.push({
				category: 'preps',
				id: 'prep1',
				time: prevDay7pm,
				label: '1st Prep',
				description: 'Mix seven (7) capfuls (119 grams) of Polyethylene glycol (MiraLAX®, ClearLax, PureLax, GaviLax, LavaClear brands) with 32 oz. of water or non-red electrolyte replacement drink and drink all contents.',
				completed: isStepCompleted(prevDay7pm)
			});
			
			steps.push({
				category: 'preps',
				id: 'mylicon-reminder',
				time: prevDay930pm,
				label: 'Reminder',
				description: 'Take 0.6 ml of Mylicon® drops mixed well with 8 oz of water.',
				completed: isStepCompleted(prevDay930pm)
			});
			
			steps.push({
				category: 'preps',
				id: 'no-drink-reminder',
				time: prevDay10pm,
				label: 'Reminder',
				description: 'Nothing to drink after 10pm. Small sips of water with medications allowed.',
				completed: isStepCompleted(prevDay10pm)
			});
			
			steps.push({
				category: 'preps',
				id: 'med-reminder',
				time: prevDay10pm,
				label: 'Medication reminder',
				description: 'Hold medications until 2 hours after swallowing capsule unless your doctor deems it necessary, then take small sips of water with medications.',
				completed: isStepCompleted(prevDay10pm)
			});
			
			steps.push({
				category: 'preps',
				id: 'prep2',
				time: procDay2hrBefore,
				label: '2nd Prep',
				description: 'Two (2) hours prior to scheduled capsule swallowing time, mix 1 capful (17 grams) of Polyethylene glycol (MiraLax, ClearLax etc.) with full glass of water or non-red electrolyte replacement drink. The Polyethylene glycol bottle top is a measuring cap marked to contain 17 grams of powder when filled to the indicated line.',
				completed: isStepCompleted(procDay2hrBefore)
			});
			
		} else {
			// Afternoon procedure steps
			const prevDay7pm = new Date(prevDay);
			prevDay7pm.setHours(19, 0, 0);
			
			const prevDay10pm = new Date(prevDay);
			prevDay10pm.setHours(22, 0, 0);
			
			const procDayMidnight = new Date(procDay);
			procDayMidnight.setHours(0, 0, 0);
			
			const procDay8hrBefore = new Date(procedureDate);
			procDay8hrBefore.setHours(procDay8hrBefore.getHours() - 8);
			
			const procDay2hrBefore = new Date(procedureDate);
			procDay2hrBefore.setHours(procDay2hrBefore.getHours() - 2);
			
			// Prep steps
			steps.push({
				category: 'preps',
				id: 'light-dinner',
				time: prevDay7pm,
				label: 'Light dinner',
				description: 'Have a light dinner. Then begin clear liquid diet for the remainder of the evening and night before the procedure. (Nothing colored red to drink).',
				completed: isStepCompleted(prevDay7pm)
			});
			
			steps.push({
				category: 'preps',
				id: 'prep1',
				time: prevDay10pm,
				label: '1st Prep',
				description: 'Mix seven (7) capfuls (119 grams) of Polyethylene glycol (MiraLAX®, ClearLax, PureLax, GaviLax, LavaClear brands) with 32 oz. of water or non-red electrolyte replacement drink and drink all contents.',
				completed: isStepCompleted(prevDay10pm)
			});
			
			steps.push({
				category: 'preps',
				id: 'midnight-reminder',
				time: procDayMidnight,
				label: 'Reminder',
				description: 'Nothing to eat or drink except water during the day.',
				completed: isStepCompleted(procDayMidnight)
			});
			
			steps.push({
				category: 'preps',
				id: 'med-reminder',
				time: procDayMidnight,
				label: 'Medication reminder',
				description: 'Hold medications until 2 hours after swallowing capsule unless your doctor deems it necessary, then take small sips of water with medications.',
				completed: isStepCompleted(procDayMidnight)
			});
			
			steps.push({
				category: 'preps',
				id: 'mylicon-reminder',
				time: procDay8hrBefore,
				label: 'Reminder',
				description: 'Take 0.6 ml of Mylicon® drops mixed well with 8 oz of water.',
				completed: isStepCompleted(procDay8hrBefore)
			});
			
			steps.push({
				category: 'preps',
				id: 'prep2',
				time: procDay2hrBefore,
				label: '2nd Prep',
				description: 'Two (2) hours prior to scheduled capsule swallowing time, mix 1 capful (17 grams) of Polyethylene glycol (MiraLax, ClearLax etc.) with full glass of water or non-red electrolyte replacement drink. The Polyethylene glycol bottle top is a measuring cap marked to contain 17 grams of powder when filled to the indicated line.',
				completed: isStepCompleted(procDay2hrBefore)
			});
		}
		
		// Ingestion steps (same for both morning and afternoon)
		steps.push({
			category: 'ingestion',
			id: 'ingestion',
			time: ingestionTime,
			label: 'Ingestion',
			description: 'Swallow CapsoCam Plus capsule with 0.6 ml of Mylicon drops in 8 oz of water.',
			completed: isStepCompleted(ingestionTime)
		});
		
		const twoHoursAfter = new Date(ingestionTime);
		twoHoursAfter.setHours(twoHoursAfter.getHours() + 2);
		
		steps.push({
			category: 'ingestion',
			id: 'resume-liquids',
			time: twoHoursAfter,
			label: 'Resume clear liquids',
			description: 'Resume clear liquids 2 hours after swallowing the capsule.',
			completed: isStepCompleted(twoHoursAfter),
		});
		
		const fourHoursAfter = new Date(ingestionTime);
		fourHoursAfter.setHours(fourHoursAfter.getHours() + 4);
		
		steps.push({
			category: 'ingestion',
			id: 'miralax-dose',
			time: fourHoursAfter,
			label: 'Take Polyethylene glycol',
			description: 'Four (4) hours after swallowing capsule, mix 17 grams of Polyethylene glycol (MiraLax, ClearLax etc.) with full glass of water or non-red electrolyte replacement drink, drink all of the contents.',
			completed: isStepCompleted(fourHoursAfter),
		});
		
		steps.push({
			category: 'ingestion',
			id: 'light-meal',
			time: new Date(fourHoursAfter.getTime() + 30 * 60 * 1000), // 30 minutes after last dose
			label: 'Light meal',
			description: 'You can enjoy a light lunch/dinner any time after drinking the last dose of Polyethylene glycol (MiraLax, ClearLax, etc.). Please document what you have for lunch.',
			completed: isStepCompleted(new Date(fourHoursAfter.getTime() + 30 * 60 * 1000)),
		});
		
		
		return steps;
	};
	
	const stepTimes = createStepTimes();
	
	// update the current time periodically
	useEffect(() => {
		// Update time every minute
		const timer = setInterval(() => {
			setCurrentTime(new Date());
		}, 15000); // 15 seconds
		
		// Clean up on unmount
		return () => clearInterval(timer);
	}, []);
	
	// Function to check if step is completed - now uses currentTime state
	function isStepCompleted(stepTime) {
		return currentTime > stepTime;
	}
	
	// Find the next step
	const getNextStep = () => {
		const now = new Date();
		const futureSteps = stepTimes.filter(step => step.time > now);
		
		if (futureSteps.length === 0) {
			// If no future steps, return the last step
			return stepTimes[stepTimes.length - 1];
		}
		
		// Sort by time and get the first one
		return futureSteps.sort((a, b) => a.time - b.time)[0];
	};
	
	const nextStep = getNextStep();
	
	// Format date and time
	const formatDate = (date) => {
		return new Intl.DateTimeFormat('en-US', { 
			month: 'short', 
			day: 'numeric'
		}).format(date);
	};

	const formatTime = (date) => {
		return new Intl.DateTimeFormat('en-US', {
			hour: 'numeric',
			minute: 'numeric',
			hour12: true
		}).format(date);
	};
	
	// Check if a category is completed
	const isCategoryCompleted = (category) => {
		const categorySteps = stepTimes.filter(step => step.category === category);
		return categorySteps.every(step => step.completed);
	};
	
	// Find all active steps for a category
	const getActiveSteps = (category) => {
		return stepTimes
			.filter(step => step.category === category && !step.completed)
			.sort((a, b) => a.time - b.time);
	};
	
	// Render the right panel instructions
	const renderInstructions = () => {
		let instructionsContent;
		let imageUrl;
		let imageAlt;

		switch(selectedStep) {
			case 'preps':
				imageUrl = mediaUrls.capsoCamCapsule;
				imageAlt = "CapsoCam Capsule";
				instructionsContent = (
					<Box>
						<Typography variant="h5" color="primary" sx={{ mb: 2 }}>
							Preps Instructions
						</Typography>
						
						<Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
							<Tooltip title="Right-click and select 'Save link as...' to download" arrow>
								<MuiLink 
									href={isMorningProcedure ? mediaUrls.morningPrepInstructions : mediaUrls.afternoonPrepInstructions}
									target="_blank"
									color="primary"
									sx={{ textDecoration: 'underline', display: 'flex', alignItems: 'center', mb: 1 }}
								>
									<PictureAsPdfIcon sx={{ mr: 1, fontSize: '20px' }} />
									Preps Instructions
									<GetAppIcon sx={{ ml: 1, fontSize: '18px' }} />
								</MuiLink>
							</Tooltip>
							
							<Tooltip title="Right-click and select 'Save link as...' to download" arrow>
								<MuiLink 
									href={mediaUrls.patientBrochure}
									target="_blank"
									color="primary" 
									sx={{ textDecoration: 'underline', display: 'flex', alignItems: 'center' }}
								>
									<PictureAsPdfIcon sx={{ mr: 1, fontSize: '20px' }} />
									Patient Brochure
									<GetAppIcon sx={{ ml: 1, fontSize: '18px' }} />
								</MuiLink>
							</Tooltip>
						</Box>
					</Box>
				);
				break;
				
			case 'ingestion':
				imageUrl = mediaUrls.openedCapsuleHolderWithCapsule;
				imageAlt = "Opened Capsule Holder With Capsule";
				instructionsContent = (
					<Box>
						<Typography variant="h5" color="primary" sx={{ mb: 2 }}>
							Ingestion Instructions
						</Typography>
						
						<Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
							<Tooltip title="Right-click and select 'Save link as...' to download" arrow>
								<MuiLink 
									href={mediaUrls.ingestionInstructions}
									target="_blank"
									color="primary"
									sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
								>
									<PictureAsPdfIcon sx={{ mr: 1, fontSize: '20px' }} />
									Ingestion Instructions
									<GetAppIcon sx={{ ml: 1, fontSize: '18px' }} />
								</MuiLink>
							</Tooltip>
							
							<Tooltip title="Right-click and select 'Save link as...' to download" arrow>
								<MuiLink 
									href={mediaUrls.patientBrochure}
									target="_blank"
									color="primary"
									sx={{ display: 'flex', alignItems: 'center' }}
								>
									<PictureAsPdfIcon sx={{ mr: 1, fontSize: '20px' }} />
									Patient Brochure
									<GetAppIcon sx={{ ml: 1, fontSize: '18px' }} />
								</MuiLink>
							</Tooltip>
						</Box>
					</Box>
				);
				break;
				
			case 'retrieval':
				imageUrl = mediaUrls.retrievalKit;
				imageAlt = "Retrieval Kit";
				instructionsContent = (
					<Box>
						<Typography variant="h5" color="primary" sx={{ mb: 2 }}>
							Retrieval Instructions
						</Typography>
						
						<Typography variant="body1" paragraph>
							How to Collect the CapsoCam Capsule Endoscope
						</Typography>
						
						<Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
							<MuiLink 
								href={mediaUrls.retrievalInstructions}
								target="_blank"
								sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
							>
								<VideocamIcon sx={{ mr: 1, fontSize: '20px' }} />
								Retrieval Video Instructions
							</MuiLink>
							
							<Tooltip title="Right-click and select 'Save link as...' to download" arrow>
								<MuiLink 
									href={mediaUrls.patientBrochure}
									target="_blank"
									color="primary"
									sx={{ display: 'flex', alignItems: 'center' }}
								>
									<PictureAsPdfIcon sx={{ mr: 1, fontSize: '20px' }} />
									Patient Brochure
									<GetAppIcon sx={{ ml: 1, fontSize: '18px' }} />
								</MuiLink>
							</Tooltip>
						</Box>
					</Box>
				);
				break;
				
			default:
				instructionsContent = null;
				imageUrl = null; // Or a default image
				imageAlt = "";
		}

		return (
			<>
				{instructionsContent}
				{imageUrl && (
					<Box sx={{ mt: 4, textAlign: 'center' }}>
						<img 
							src={imageUrl} 
							alt={imageAlt} 
							style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }} 
							draggable="false"
						/>
					</Box>
				)}
			</>
		);
	};

	return (
		<>
			{currentTime > threeDaysBefore && curProcedure.status !== 'ingested' && (
				<Alert severity="warning" sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
					<Box sx={{ flexGrow: 1, mr: 2, display: 'flex', alignItems: 'center' }}>
						<span>
							If the capsule package has not been received 3 days before the appointment time, please contact your HCP {curProcedure.officePhone ? `(${curProcedure.officePhone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')})` : ''}.
						</span>
						<img 
							src={mediaUrls.capsoCamBox}
							alt="CapsoCam Box"
							style={{ maxHeight: '64px', flexShrink: 0, marginLeft: '8px' }}
							draggable="false"
						/>
					</Box>
				</Alert>
			)}
			<Grid container spacing={2} sx={{ height: '100%' }}>
				{/* Left Panel - Todo List */}
				<Grid item xs={4}>
					<Paper sx={{ p: 2, height: '100%', border: 1, borderColor: 'primary.main' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
							<Typography variant="h5" color="primary" sx={{ flexGrow: 1 }}>
								Todo List
							</Typography>
							{nextStep && (
								<CountdownTimer 
									targetDate={nextStep.time} 
									showIcon={true} 
									fontSize={2} 
								/>
							)}
						</Box>
						
						<List>
							{/* Preps Item */}
							<ListItem 
								button 
								onClick={() => setSelectedStep('preps')} 
								selected={selectedStep === 'preps'}
								sx={{ borderRadius: 1, mb: 1 }}
							>
								<ListItemIcon>
									{isCategoryCompleted('preps') ? 
										<CheckCircleIcon color="success" /> : 
										<CircleIcon color="primary" />}
								</ListItemIcon>
								<ListItemText 
									primary={
										<Typography variant="h6" color="primary">
											Preps for {isMorningProcedure ? 'Morning' : 'Afternoon'} Procedure
										</Typography>
									}
								/>
							</ListItem>
							
							{/* Preps Sublist */}
							<List component="div" disablePadding>
								{getActiveSteps('preps').map((step) => (
									<ListItem 
										key={step.id}
										sx={{ pl: 2 }}
									>
										<ListItemText
											primary={
												<Typography variant="body1" sx={{ color: step.id === nextStep?.id ? 'secondary.main' : 'inherit' }}>
													<Typography component="span" fontWeight="bold">
														{formatDate(step.time)}, {formatTime(step.time)}:
													</Typography>
													{" "}{step.label}
												</Typography>
											}
											secondary={step.description}
										/>
									</ListItem>
								))}
							</List>
							
							{/* Ingestion Item */}
							<ListItem 
								button 
								onClick={() => setSelectedStep('ingestion')} 
								selected={selectedStep === 'ingestion'}
								sx={{ borderRadius: 1, mb: 1 }}
							>
								<ListItemIcon>
									{curProcedure.status === 'ingested' ? 
										<CheckCircleIcon color="success" /> : 
										<CircleIcon color="primary" />}
								</ListItemIcon>
								<ListItemText 
									primary={
										<Typography variant="h6" color="primary">
											Ingestion
										</Typography>
									}
								/>
							</ListItem>
							
							{/* Ingestion Sublist */}
							<List component="div" disablePadding>
								{getActiveSteps('ingestion').map((step) => (
									<ListItem 
										key={step.id}
										sx={{ pl: 2 }}
									>
										<ListItemText
											primary={
												<Typography variant="body1" sx={{ color: step.id === nextStep?.id ? 'secondary.main' : 'inherit' }}>
													<Typography component="span" fontWeight="bold">
														{formatDate(step.time)}, {formatTime(step.time)}:
													</Typography>
													{" "}{step.label}
												</Typography>
											}
											secondary={step.description}
										/>
										{step.countUp && isStepCompleted(step.time) && (
											<CountdownTimer 
												targetDate={step.time} 
												showIcon={false} 
												fontSize={1} 
												countUp={true}
											/>
										)}
									</ListItem>
								))}
							</List>
							
							{/* Retrieval Item */}
							<ListItem 
								button 
								onClick={() => setSelectedStep('retrieval')} 
								selected={selectedStep === 'retrieval'}
								sx={{ borderRadius: 1 }}
							>
								<ListItemIcon>
									<CircleIcon color="primary" />
								</ListItemIcon>
								<ListItemText 
									primary={
										<Typography variant="h6" color="primary">
											Retrieval
										</Typography>
									}
								/>
							</ListItem>
							
						</List>
					</Paper>
				</Grid>
				
				{/* Middle Panel - Video Call or Ingestion Status */}
				<Grid item xs={4}>
					{curProcedure.status === 'ingested' && curProcedure.ingestionTime ? (
						<Paper sx={{ p: 2, height: '100%', border: 1, borderColor: 'primary.main', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
							{/* Ingestion Completed View */}
							<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', mb: 2 }}>
								<CountdownTimer 
									targetDate={new Date(curProcedure.ingestionTime)} 
									showIcon={true} 
									fontSize={3} 
									countUp={true} 
								/>
								<Typography variant="subtitle1" color="text.secondary">
									since ingestion
								</Typography>
							</Box>
							
							<Typography variant="h4" color="primary" sx={{ mb: 3, textAlign: 'center' }}>
								Ingestion Completed!
							</Typography>
							
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
								<Box component="span" sx={{ color: 'success.main', mr: 1 }}>
									→
								</Box>
								<Tooltip title="Download Shipping Label">
									<Button variant="outlined" onClick={downloadShippingLabel} >
										<CapsoIcon iconName="action-downloadshippinglabel" size="md" />
									</Button>
								</Tooltip>
								
							</Box>
							
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ fontStyle: 'italic', mb: 2, textAlign: 'center' }}
							>
								Download Shipping Label if you lose your original one
							</Typography>
							
							<Box sx={{ mt: 4, textAlign: 'center' }}>
								<img 
									src={mediaUrls.fedexLogo}
									alt="FedEx Logo"
									style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
									draggable="false"
								/>
							</Box>
							
						</Paper>
					) : (
						<Paper sx={{ p: 2, height: '100%', border: 1, borderColor: 'primary.main' }}>
							<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
								<CountdownTimer 
									targetDate={curProcedure.procedureDate} 
									expiredText="Time for Telehealth Video Call" 
								/>
							</Box>
							
							<Box sx={{ textAlign: 'center', mt: 4 }}>
								<Typography variant="h5" color="primary">
									Upcoming Appointment at
								</Typography>
								<Typography variant="subtitle1" component="div">
									{/* procedure date and time */}
									{moment(curProcedure.procedureDate).format('MMM D, h:mm A')}
								</Typography>
								<Typography variant="subtitle1" component="div">
									with Dr. {curProcedure.physicianFullName}
								</Typography>
								<Typography variant="subtitle1" component="div">
									SN: {curProcedure.sn}
								</Typography>
							</Box>
							
							<Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
								{curProcedure.videoCallEnabled ? (
									<Tooltip title="Join Video Call">
										<Link to="/vidconf" state={{ procedure: curProcedure }} style={{ textDecoration: 'none' }}>
											<Button
												variant={moment().isAfter(moment(curProcedure.procedureDate)) ? "contained" : "outlined"}
												color="primary"
												startIcon={<CapsoIcon iconName="action-videocall" size="lg" />}
												sx={{
													py: 1.5,
													px: 3,
													borderRadius: 1,
													fontSize: '1rem',
													textTransform: 'none',
													backgroundColor: moment().isAfter(moment(curProcedure.procedureDate)) ? 'primary.main' : 'white',
													borderColor: 'primary.main',
													color: moment().isAfter(moment(curProcedure.procedureDate)) ? 'white' : 'primary.main',
													'&:hover': {
														backgroundColor: moment().isAfter(moment(curProcedure.procedureDate)) 
															? 'primary.dark' 
															: 'rgba(255, 255, 255, 0.9)',
														borderColor: 'primary.dark',
														color: moment().isAfter(moment(curProcedure.procedureDate)) ? 'white' : 'primary.dark',
													}
												}}
											>
												Join Video Call
											</Button>
										</Link>
									</Tooltip>
								) : (
									<Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
										Please use the software/application provided by your physician&apos;s office for the Telehealth appointment
									</Typography>
								)}
							</Box>
							
							<Box sx={{ textAlign: 'center', mt: 4 }}>
								<Typography variant="h6">
									For emergency, call office: <br />
									<PhoneIcon sx={{ fontSize: 'large', mr: 0.5 }} />
									{curProcedure.officePhone && curProcedure.officePhone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
								</Typography>
							</Box>
						</Paper>
					)}
				</Grid>
				
				{/* Right Panel - Instructions */}
				<Grid item xs={4}>
					<Paper sx={{ p: 2, height: '100%', border: 1, borderColor: 'primary.main' }}>
						{renderInstructions()}
					</Paper>
				</Grid>
			</Grid>
		</>
	);
};

export default ProcedureScheduledOrIngested;